import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { apiClient } from '@zaogig/auth';
import { useGeolocation } from './use-geolocation';
import type { TodayAttendanceData, CheckInResultData, CheckOutResultData, TaskQuestionResponse, SummaryQuestionResponse } from './use-attendance-state';

interface UseAttendanceBusinessOptions {
  shiftId?: string;
  jobId?: string;
  onComplete?: (success: boolean) => void;
}

interface UseAttendanceBusinessReturn {
  todayAttendance: TodayAttendanceData | null;
  location: ReturnType<typeof useGeolocation>['location'];
  locationError: ReturnType<typeof useGeolocation>['error'];
  locationLoading: boolean;
  loadAttendanceStatus: () => Promise<void>;
  getCheckInQuestion: () => Promise<TaskQuestionResponse>;
  submitCheckIn: (params: {
    userAnswer: string;
    question: TaskQuestionResponse;
  }) => Promise<CheckInResultData>;
  getCheckOutQuestion: () => Promise<SummaryQuestionResponse>;
  submitCheckOut: (workSummary: string) => Promise<CheckOutResultData>;
  ensureLocation: () => Promise<boolean>;
  startHeartbeat: (params: { shiftId?: string; jobId?: string }) => () => void;
}

export function useAttendanceBusiness(
  options: UseAttendanceBusinessOptions
): UseAttendanceBusinessReturn {
  const { shiftId, jobId, onComplete } = options;
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendanceData | null>(null);

  const {
    location,
    error: locationError,
    isLoading: locationLoading,
    getLocation
  } = useGeolocation();

  const loadAttendanceStatus = useCallback(async (): Promise<void> => {
    try {
      const data = await apiClient.get<any>('/attendance/today');
      const adaptedData: TodayAttendanceData = {
        date: data.date,
        shiftId: data.shiftId,
        jobId: data.jobId,
        checkInTime: data.checkInTime,
        checkOutTime: data.checkOutTime,
        canCheckIn: data.canCheckIn,
        canCheckOut: data.canCheckOut,
        workHours: data.workHours,
        workMinutes: data.workMinutes,
        estimatedIncome: data.estimatedIncome
      };
      setTodayAttendance(adaptedData);
    } catch (error) {
      console.error('Failed to load attendance status:', error);
    }
  }, []);

  const ensureLocation = useCallback(async (): Promise<boolean> => {
    if (location) {
      return true;
    }

    try {
      const newLocation = await getLocation();
      return newLocation !== null;
    } catch (error) {
      toast.error('请允许位置权限以进行打卡');
      return false;
    }
  }, [location, getLocation]);

  const getCheckInQuestion = useCallback(async (): Promise<TaskQuestionResponse> => {
    const hasLocation = await ensureLocation();
    if (!hasLocation) {
      throw new Error('位置信息获取失败');
    }

    const question = await apiClient.post<TaskQuestionResponse>('/attendance/task-question', { jobId, shiftId });
    return question;
  }, [ensureLocation, jobId, shiftId]);

  const submitCheckIn = useCallback(async (params: {
    userAnswer: string;
    question: TaskQuestionResponse;
  }): Promise<CheckInResultData> => {
    if (!location) {
      throw new Error('位置信息未获取');
    }

    const result = await apiClient.post<any>('/attendance/task-checkin', {
      shiftId,
      jobId,
      lat: location.lat,
      lng: location.lng,
      accuracy: location.accuracy,
      deviceId: navigator.userAgent.slice(0, 50),
      userAnswer: params.userAnswer,
      question: params.question.question,
      expectedKeywords: params.question.expectedKeywords
    });

    const adaptedResult: CheckInResultData = {
      attendanceId: result.attendanceId,
      gpsPassed: result.gpsPassed,
      voicePassed: result.voicePassed,
      taskUnderstandingScore: result.taskUnderstandingScore,
      riskScore: result.riskScore,
      result: result.result
    };

    await loadAttendanceStatus();
    toast.success('上班打卡成功');

    return adaptedResult;
  }, [location, shiftId, jobId, loadAttendanceStatus]);

  const getCheckOutQuestion = useCallback(async (): Promise<SummaryQuestionResponse> => {
    const hasLocation = await ensureLocation();
    if (!hasLocation) {
      throw new Error('位置信息获取失败');
    }

    const question = await apiClient.post<SummaryQuestionResponse>('/attendance/summary-question');
    return question;
  }, [ensureLocation]);

  const submitCheckOut = useCallback(async (workSummary: string): Promise<CheckOutResultData> => {
    if (!location) {
      throw new Error('位置信息未获取');
    }

    const result = await apiClient.post<any>('/attendance/task-checkout', {
      shiftId,
      jobId,
      lat: location.lat,
      lng: location.lng,
      workSummary
    });

    const adaptedResult: CheckOutResultData = {
      checkoutId: result.checkoutId,
      workMinutes: result.workMinutes,
      summaryScore: result.summaryScore,
      result: result.result
    };

    toast.success('下班打卡成功');

    if (onComplete) {
      setTimeout(() => onComplete(true), 2000);
    }

    return adaptedResult;
  }, [location, shiftId, jobId, onComplete]);

  const startHeartbeat = useCallback((params: { shiftId?: string; jobId?: string }) => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const sendHeartbeat = async () => {
      if (!location) return;

      try {
        await apiClient.post('/fulfillment/heartbeat', {
          shiftId: params.shiftId,
          jobId: params.jobId,
          lat: location.lat,
          lng: location.lng,
          isOnline: true
        });
      } catch (error) {
        console.error('Failed to send heartbeat:', error);
      }
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(sendHeartbeat, 5 * 60 * 1000);
      sendHeartbeat();
    };

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    start();

    return stop;
  }, [location]);

  return {
    todayAttendance,
    location,
    locationError,
    locationLoading,
    loadAttendanceStatus,
    getCheckInQuestion,
    submitCheckIn,
    getCheckOutQuestion,
    submitCheckOut,
    ensureLocation,
    startHeartbeat
  };
}

export function useAttendanceTimeTracker() {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return currentTime;
}

export function useWorkDurationCalculator(workStartTime: Date | null, currentTime: Date) {
  const calculateDuration = useCallback(() => {
    if (!workStartTime) return null;

    const diff = currentTime.getTime() - workStartTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes };
  }, [workStartTime, currentTime]);

  return calculateDuration();
}
